/**
 * Deals (Allow Bot Collusion), for humans.
 *
 * - `DealPrompt`: a deal waiting on your answer (an offer to you, or a counter to yours) pops up
 *   over the table with its terms, a countdown (`DEAL_ANSWER_MS`; the server declines it when
 *   time is up) and Accept / Decline / Whole Round? (counter: offers until the last pass only).
 * - `DealOffer`: your turn, after rolling (the Deal button in `TurnPanel`): pick who to offer a
 *   deal to, then who you're after (the card your dice threaten most is picked to start with).
 *   Your turn waits for the answer.
 *
 * Beginner Mode adds a note on what a deal is worth (nothing binds it). Everything said goes to
 * the chat as lines from the players (the server's), so it shows there too.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DEAL_ANSWER_MS,
  MAX_SAVED_DICE,
  MAX_SEQUENCES,
  canKill,
  dealResponder,
  isPending,
  killOdds,
  t,
  type CardDef,
  type CardId,
  type Deal,
  type GameView,
  type PlayerId,
  type PublicPlayer,
} from '@kadice/shared';
import type { GameClient } from '../useGameClient';

/** The deal waiting on an answer, if any. */
export const pendingDeal = (view: GameView): Deal | null => view.deals.find(isPending) ?? null;

/** Players you can offer a deal to now: anyone else with a card you have no deal with this round. */
export function dealPartners(view: GameView): PublicPlayer[] {
  const me = view.you.id;
  const dealt = new Set(view.deals.flatMap((d) => (d.from === me ? [d.to] : d.to === me ? [d.from] : [])));
  return view.players.filter((p) => p.id !== me && p.hasCard && p.status !== 'out' && !dealt.has(p.id));
}

/** Seconds left to answer the waiting deal (counting down between views), or null when nobody is timed. */
export function useDealSeconds(view: GameView): number | null {
  const [tick, setTick] = useState<{ view: GameView; elapsed: number } | null>(null);
  const timed = view.dealMs !== null;
  useEffect(() => {
    if (!timed) return;
    const at = Date.now();
    const id = setInterval(() => setTick({ view, elapsed: Date.now() - at }), 250);
    return () => clearInterval(id);
  }, [view, timed]);
  if (view.dealMs === null) return null;
  const elapsed = tick?.view === view ? tick.elapsed : 0;
  return Math.max(0, Math.ceil((view.dealMs - elapsed) / 1000));
}

const nameOf = (view: GameView, id: PlayerId) => view.players.find((p) => p.id === id)?.name ?? '?';

/** The countdown under a deal: seconds left and a bar running down. */
function DealClock({ seconds }: { seconds: number | null }) {
  if (seconds === null) return null;
  return (
    <div className="deal-clock" aria-label={t('deal.seconds', { n: seconds })}>
      <div
        className="deal-clock-bar"
        style={{ width: `${Math.min(100, ((seconds * 1000) / DEAL_ANSWER_MS) * 100)}%` }}
      />
      <span>{t('deal.seconds', { n: seconds })}</span>
    </div>
  );
}

/** A deal waiting on your answer: its terms, the countdown, and your answers. */
export function DealPrompt({ view, client, beginner }: { view: GameView; client: GameClient; beginner: boolean }) {
  const deal = pendingDeal(view);
  const seconds = useDealSeconds(view);
  const [sending, setSending] = useState(false);
  if (!deal || dealResponder(deal) !== view.you.id || view.you.spectating) return null;
  const countered = deal.status === 'countered';
  const other = nameOf(view, countered ? deal.to : deal.from);
  const answer = async (a: 'accept' | 'decline' | 'counter') => {
    setSending(true);
    await client.request('game:answerDeal', { answer: a });
    setSending(false);
  };
  return createPortal(
    <div className="modal-backdrop kill-warn-backdrop">
      <div className="modal kill-warn-box deal-box" role="alertdialog" aria-modal="true">
        <h2>{t(countered ? 'deal.counterTitle' : 'deal.incomingTitle', { name: other })}</h2>
        <p>
          {countered
            ? t('deal.counterBody', { name: other })
            : t(deal.spare === 'pass' ? 'deal.incomingBody' : 'deal.incomingBodyRound', {
                name: other,
                target: nameOf(view, deal.target),
              })}
        </p>
        {beginner && <p className="deal-note">{t('deal.beginnerNote')}</p>}
        <DealClock seconds={seconds} />
        <div className="row actions">
          <button disabled={sending} onClick={() => void answer('decline')}>
            {t('deal.decline')}
          </button>
          {!countered && deal.spare === 'pass' && (
            <button disabled={sending} title={t('deal.counterHint')} onClick={() => void answer('counter')}>
              {t('deal.counter')}
            </button>
          )}
          <button className="primary" autoFocus disabled={sending} onClick={() => void answer('accept')}>
            {t('deal.accept')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Offer a deal: who to, then who you're after. `dice`: your dice on the table (power changes
 * included), `kept`: the ones you hold; together they pick the card you're likeliest to kill.
 */
export function DealOffer({
  view,
  client,
  catalog,
  power,
  dice,
  kept,
  beginner,
  onClose,
}: {
  view: GameView;
  client: GameClient;
  catalog: ReadonlyMap<CardId, CardDef>;
  power: string | null;
  dice: readonly number[];
  kept: readonly number[];
  beginner: boolean;
  onClose: () => void;
}) {
  const partners = dealPartners(view);
  const [partner, setPartner] = useState<PlayerId | null>(null);
  const [picked, setPicked] = useState<PlayerId | null>(null);
  const [sending, setSending] = useState(false);
  const targets = view.players.filter((p) => p.id !== view.you.id && p.id !== partner && p.hasCard);
  // Your best shot: a card the dice already kill, else the best odds from the dice you hold.
  const rolls = MAX_SEQUENCES - view.sequence;
  const score = (p: PublicPlayer) => {
    const def = p.card === null ? undefined : catalog.get(p.card);
    if (!def) return 0;
    if (canKill(dice, def.killer)) return 1;
    return killOdds(def.killer, power, rolls, kept.slice(0, MAX_SAVED_DICE));
  };
  const best = targets.map((p) => ({ id: p.id, odds: score(p) })).sort((a, b) => b.odds - a.odds)[0]?.id ?? null;
  const target = picked !== null && targets.some((p) => p.id === picked) ? picked : best;
  const cardName = (p: PublicPlayer) => (p.card === null ? '' : (catalog.get(p.card)?.name ?? ''));

  const send = async () => {
    if (partner === null || target === null) return;
    setSending(true);
    await client.request('game:offerDeal', { to: partner, target });
    setSending(false);
    onClose();
  };

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal deal-box" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h2>{t('deal.offerTitle')}</h2>
        {partner === null ? (
          <>
            <p>{partners.length > 0 ? t('deal.pickPartner') : t('deal.noPartners')}</p>
            <div className="deal-choices">
              {partners.map((p) => (
                <button key={p.id} onClick={() => setPartner(p.id)}>
                  <strong>{p.name}</strong> <span className="muted small">{cardName(p)}</span>
                </button>
              ))}
            </div>
            <div className="row actions">
              <button onClick={onClose}>{t('common.cancel')}</button>
            </div>
          </>
        ) : (
          <>
            <p>{t('deal.pickTarget')}</p>
            <div className="deal-choices">
              {targets.map((p) => (
                <button
                  key={p.id}
                  className={p.id === target ? 'primary' : undefined}
                  aria-pressed={p.id === target}
                  onClick={() => setPicked(p.id)}
                >
                  <strong>{p.name}</strong> <span className="small">{cardName(p)}</span>
                  {p.id === best && <span className="small"> ({t('deal.likeliest')})</span>}
                </button>
              ))}
            </div>
            {target !== null && (
              <p className="deal-terms">
                {t(view.sequence === 1 ? 'chat.deal.offer' : 'chat.deal.offerRound', {
                  to: nameOf(view, partner),
                  target: nameOf(view, target),
                })}
              </p>
            )}
            {beginner && <p className="deal-note">{t('deal.offerNote')}</p>}
            <div className="row actions">
              <button onClick={() => setPartner(null)}>{t('deal.back')}</button>
              <button className="primary" disabled={sending || target === null} onClick={() => void send()}>
                {t('deal.send')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
